/* eslint-disable no-unused-vars */
import React, { useEffect, useContext, lazy, Suspense } from 'react';
import { Layout } from 'antd';
import { isEmpty } from 'lodash';
import { Navigate, Route, Routes, Outlet, useLocation } from 'react-router-dom';

import Loading from 'components/Loading';
import Sidebar from 'components/Sidebar/index';
import { TENANT_TYPE } from 'constants/Common';
import useProfile from 'hooks/data/useProfile';
import useTenant from 'hooks/data/useTenant';
import AppHeader from 'pages/App/Header';
import HkdBooksGuard from 'pages/Report/HkdBooks/HkdBooksGuard';
import SopGuard from 'pages/Sop/SopGuard';
import { CollapseSidebar } from 'store/actions/ConfigAction';
import { ConfigContext } from 'store/context';
import axios from 'util/Api';
import { isChatEnabled } from 'util/chatAvailability';

import { useAuth } from '../../hooks/useAuth';
import Confirmation from '../Login/Confirmation';
import ForgotPassword from '../Login/ForgotPassword';
import Invitation from '../Login/Invitation';
import RejectInvite from '../Login/RejectInvite';
import ResetPassword from '../Login/ResetPassword';
import SignIn from '../Login/SignIn';
import SignUp from '../Login/SignUp';
import SignUpV2 from '../Login/SignUpV2';

const Page404 = lazy(() => import('pages/Login/404'));
const Page500 = lazy(() => import('pages/Login/500'));
const Home = lazy(() => import('pages/DashboardPage'));
const Profile = lazy(() => import('pages/Profile'));
const POS = lazy(() => import('pages/POS'));
const POSRegister = lazy(() => import('pages/POSRegister'));
const POSOrderDetail = lazy(() => import('pages/POSOrderDetail'));
const POSSettingForm = lazy(() => import('pages/POSSettingForm'));
const POSSettingList = lazy(() => import('pages/POSSettingList'));
const QualityControl = lazy(() => import('pages/QualityControl'));
const QuantityControlForm = lazy(() => import('pages/QualityControl/QualityControlForm'));
const CustomerComplaint = lazy(() => import('pages/CustomerComplaint'));
const CustomerComplaintForm = lazy(() => import('pages/CustomerComplaint/CustomerComplaintForm'));
const AdrList = lazy(() => import('pages/Sop/Adr/AdrList'));
const AdrForm = lazy(() => import('pages/Sop/Adr/AdrForm'));
const RecallNoticeList = lazy(() => import('pages/Sop/Recall/RecallNoticeList'));
const RecallNoticeForm = lazy(() => import('pages/Sop/Recall/RecallNoticeForm'));
const HygieneLogList = lazy(() => import('pages/Sop/Hygiene/HygieneLogList'));
const TemperatureHumidityBook = lazy(() => import('pages/Sop/TemperatureHumidity/TemperatureHumidityBook'));
const HygieneLogForm = lazy(() => import('pages/Sop/Hygiene/HygieneLogForm'));
const InvalidPrescriptionList = lazy(() => import('pages/Sop/InvalidRx/InvalidPrescriptionList'));
const InvalidPrescriptionForm = lazy(() => import('pages/Sop/InvalidRx/InvalidPrescriptionForm'));
const ControlledRegisterList = lazy(() => import('pages/Sop/Controlled/ControlledRegisterList'));
const DrugReceiptsList = lazy(() => import('pages/Sop/DrugReceipts/DrugReceiptsList'));
const PrescriptionSalesList = lazy(() => import('pages/Sop/PrescriptionSales/PrescriptionSalesList'));
const CustomerDetailList = lazy(() => import('pages/Sop/CustomerDetail/CustomerDetailList'));
const PatientTracking = lazy(() => import('pages/PatientTracking/PatientTrackingList'));
const InventoryValuation = lazy(() => import('pages/Report/InventoryValuation'));
const HkdBookList = lazy(() => import('pages/Report/HkdBooks/HkdBookList'));
const SopList = lazy(() => import('pages/Sop/SopList'));
const HkdS1a = lazy(() => import('pages/Report/HkdBooks/S1aPage'));
const HkdS2a = lazy(() => import('pages/Report/HkdBooks/S2aPage'));
const HkdS2b = lazy(() => import('pages/Report/HkdBooks/S2bPage'));
const HkdS2c = lazy(() => import('pages/Report/HkdBooks/S2cPage'));
const HkdS2d = lazy(() => import('pages/Report/HkdBooks/S2dPage'));
const HkdS2e = lazy(() => import('pages/Report/HkdBooks/S2ePage'));
const HkdS3a = lazy(() => import('pages/Report/HkdBooks/S3aPage'));
const HkdDeclarationList = lazy(() => import('pages/Report/HkdBooks/HkdDeclarationList'));
const HkdStocktakeDeclaration = lazy(() => import('pages/Report/HkdBooks/StocktakeDeclarationPage'));
const HkdRevenueNotification = lazy(() => import('pages/Report/HkdBooks/RevenueNotificationPage'));
const HkdCnkdReturn = lazy(() => import('pages/Report/HkdBooks/CnkdReturnPage'));
const HkdPitFinalization = lazy(() => import('pages/Report/HkdBooks/PitFinalizationPage'));
const HkdBkStkNotice = lazy(() => import('pages/Report/HkdBooks/BkStkNoticePage'));
const HkdTbDdkdNotice = lazy(() => import('pages/Report/HkdBooks/TbDdkdNoticePage'));
const Setting = lazy(() => import('pages/SettingPage'));
const StoreSettingPage = lazy(() => import('pages/StoreSettingPage'));
const GeneralSetting = lazy(() => import('pages/GeneralSetting'));
const PurchaseOrderDetail = lazy(() => import('pages/PurchaseOrderDetail'));
const PurchaseOrderList = lazy(() => import('pages/PurchaseOrderList'));
const PurchaseOrderImportList = lazy(() => import('pages/PurchaseOrderImportList'));
const PurchaseOrderImportPreview = lazy(() => import('pages/PurchaseOrderImport'));
const ReceiptList = lazy(() => import('pages/ReceiptList'));
const ReceiptDetail = lazy(() => import('pages/ReceiptDetail'));
const SupplierReturnCreate = lazy(() => import('pages/SupplierReturn/Create'));
const SupplierReturnList = lazy(() => import('pages/SupplierReturn'));
const SupplierReturnDetail = lazy(() => import('pages/SupplierReturn/Detail'));
const SettingStaff = lazy(() => import('pages/StaffPage'));
const AddNewStaff = lazy(() => import('pages/StaffPage/Form'));
const EditStaff = lazy(() => import('pages/StaffPage/Form'));
const Subscription = lazy(() => import('pages/SubscriptionPage'));
const CustomerList = lazy(() => import('pages/CustomerList'));
const CustomerForm = lazy(() => import('pages/CustomerForm'));
const CustomerGroupList = lazy(() => import('pages/CustomerGroupList'));
const ProductList = lazy(() => import('pages/ProductList'));
const ProductForm = lazy(() => import('pages/ProductForm'));
const CategoryList = lazy(() => import('pages/CategoryList'));
const UOMList = lazy(() => import('pages/UOMList'));
const TagList = lazy(() => import('pages/TagList'));
const VendorList = lazy(() => import('pages/VendorList'));
const VendorForm = lazy(() => import('pages/VendorForm'));
const POSOrderList = lazy(() => import('pages/POSOrderList'));
const ScrapList = lazy(() => import('pages/ScrapList'));
const ScrapForm = lazy(() => import('pages/ScrapForm'));
const ExpenseList = lazy(() => import('pages/ExpenseList'));
const ExpenseForm = lazy(() => import('pages/ExpenseForm'));
const InventoryList = lazy(() => import('pages/InventoryList'));
const InventoryForm = lazy(() => import('pages/InventoryForm'));
const InventorySetting = lazy(() => import('pages/InventorySetting'));
const TaxSetting = lazy(() => import('pages/TaxSetting'));
const PaymentSetting = lazy(() => import('pages/PaymentSetting'));
const EInvoiceSetting = lazy(() => import('pages/EInvoiceSetting'));
const HkdBookSetting = lazy(() => import('pages/HkdBookSetting'));
const ExpenseCategorySetting = lazy(() => import('pages/ExpenseCategorySetting'));
const DavIntegrationPage = lazy(() => import('pages/DavIntegrationPage'));
const HkdOpeningBalance = lazy(() => import('pages/OpeningBalancePage'));
const SetupStore = lazy(() => import('pages/SetupStore'));
const PrescriptionTemplate = lazy(() => import('pages/PrescriptionTemplate'));
const PrescriptionTemplateForm = lazy(() => import('pages/PrescriptionTemplate/PrescriptionTemplateForm'));
const SpecializationList = lazy(() => import('pages/SpecializationList'));
const Doctor = lazy(() => import('pages/Doctor'));
const DoctorForm = lazy(() => import('pages/Doctor/Form'));
const EInvoice = lazy(() => import('pages/EInvoice'));
const EInvoiceForm = lazy(() => import('pages/EInvoice/EInvoiceForm'));
const NotificationPage = lazy(() => import('pages/NotificationPage'));
const InboxDemo = lazy(() => import('pages/Demo/Inbox'));
const DemoV2Login = lazy(() => import('pages/Demo/V2/Login'));
const DemoV2SetupPharmacy = lazy(() => import('pages/Demo/V2/SetupPharmacy'));
const DemoV2SetupClinic = lazy(() => import('pages/Demo/V2/SetupClinic'));
const DemoV2SetupPharmaCompany = lazy(() => import('pages/Demo/V2/SetupPharmaCompany'));
const DemoV2Calendar = lazy(() => import('pages/Demo/V2/Calendar'));
const ChatConsole = lazy(() => import('pages/Chat'));
const DemoV2ClinicQueue = lazy(() => import('pages/Demo/V2/ClinicQueue'));
const DemoV2ClinicWebsite = lazy(() => import('pages/Demo/V2/ClinicWebsite'));
const DemoV2ClinicFeatures = lazy(() => import('pages/Demo/V2/ClinicFeatures'));
const DemoV2ClinicRoadmap = lazy(() => import('pages/Demo/V2/ClinicRoadmap'));
const PharmacySettingV2 = lazy(() => import('pages/PharmacySettingV2'));
const ClinicSettingV2 = lazy(() => import('pages/ClinicSettingV2'));
const isDevelopment = process.env.NODE_ENV === 'development';

const { Content } = Layout;
const routeNoSidebar = [
  '/pos',
  '/demo/inbox',
  '/demo/v2/setup-pharmacy',
  '/demo/v2/setup-clinic',
  '/demo/v2/setup-pharma-company',
  '/demo/v2/clinic-features',

  '/setup-store',
  '/setup/pharmacy',
  '/setup/clinic',
  '/404',
  '/500',
];
const routeForError = ['/404', '/500'];

function WaitingComponent(Component, propsCustom) {
  return (
    <Suspense fallback={<Loading />}>
      <AppHeader {...propsCustom} />
      <Component {...propsCustom} />
    </Suspense>
  );
}

function WaitingComponentPOS(Component, propsCustom) {
  return (
    <Suspense fallback={<Loading />}>
      <Component {...propsCustom} />
    </Suspense>
  );
}

function renderSidebar() {
  if (routeNoSidebar.includes(window.location.pathname)) {
    return null;
  }

  return <Sidebar />;
}

function RequireAuth(props) {
  const { isAuthenticated } = props;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      {routeForError.includes(window.location.pathname) ? (
        <Outlet />
      ) : (
        <Layout className="omi-app-layout">
          {renderSidebar()}
          <Layout className={`bg-grey-200  ${window.location.pathname === '/pos' ? 'pos' : ''}`}>
            <Content>
              <Outlet />
            </Content>
          </Layout>
        </Layout>
      )}
    </>
  );
}

function SubscriptionRequired({ subscription, loadingTenant }) {
  if (window.location.pathname === '/settings/subscriptions' || loadingTenant) {
    return <Outlet />;
  }

  // const isActive = [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL].includes(subscription.status_name);
  // if (!isActive) {
  //   return <Navigate to="/settings/subscriptions" state={{ showPaidModal: true }} />;
  // }
  return (
    <>
      <Outlet />
    </>
  );
}

export default function () {
  const { isAuthenticated, token, initURL, user } = useAuth();

  const location = useLocation();
  const { mutate } = useProfile();
  const {
    tenant,
    mutate: mutateTenant,
    loading: loadingTenant,
  } = useTenant({ revalidateOnFocus: false, revalidateOnMount: false });

  const {
    ConfigState: { statusCollapse: status },
    ConfigDispatch,
  } = useContext(ConfigContext);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && !isEmpty(user)) {
      mutate();
      mutateTenant();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (routeNoSidebar.includes(window.location.pathname)) {
      ConfigDispatch(CollapseSidebar(false));
    } else {
      ConfigDispatch(CollapseSidebar(true));
    }
  }, [location.pathname]);

  if (window.location.pathname === '/') {
    if (token === null && isEmpty(user)) {
      return <Navigate to="/login" />;
    }

    if (initURL === '' || initURL === '/' || initURL === '/login') {
      return <Navigate to="/dashboard" />;
    }

    return <Navigate to={initURL} />;
  }

  return (
    <Routes>
      <Route path="/">
        <Route element={<SignIn />} />
        <Route path="Login" element={<SignIn />} />
        <Route path="confirm" element={<Confirmation />} />
        <Route path="forgot-password" element={<ForgotPassword />} />

        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="signup" element={<SignUp />} />
        {isDevelopment && <Route path="signup-v2" element={<SignUpV2 />} />}
        <Route path="clinic/signup-v2" element={<SignUpV2 key="clinic" initialTenantType={TENANT_TYPE.CLINIC} />} />
        <Route
          path="pharmacy/signup-v2"
          element={<SignUpV2 key="pharmacy" initialTenantType={TENANT_TYPE.PHARMACY} />}
        />
        <Route path="invitations" element={<Invitation />} />
        <Route path="invitations/reject" element={<RejectInvite />} />
        {/* Demo V2 public routes */}
        <Route
          path="demo/v2/login"
          element={
            <Suspense fallback={<Loading />}>
              <DemoV2Login />
            </Suspense>
          }
        />
        {/* <RestrictedRoute isAuthenticated={isAuthenticated} element={<MainApp />} /> */}
        <Route path="*" element={<RequireAuth isAuthenticated={isAuthenticated} />}>
          <Route path="404" element={<Page404 />} />
          <Route path="500" element={<Page500 />} />
          <Route path="setup-store" element={<SetupStore />} />
          <Route path="setup/pharmacy" element={WaitingComponentPOS(PharmacySettingV2)} />
          <Route path="setup/clinic" element={WaitingComponentPOS(ClinicSettingV2)} />

          <Route path="settings" element={<Outlet />}>
            <Route index element={WaitingComponent(Setting, { status })} />
            <Route
              path="subscriptions"
              element={WaitingComponent(Subscription, {
                status,
                redirect: { name: 'Cấu hình', link: '/settings' },
              })}
            />
          </Route>
          <Route
            path="*"
            element={<SubscriptionRequired subscription={tenant?.subscription || {}} loadingTenant={loadingTenant} />}
          >
            <Route path="dashboard" element={WaitingComponent(Home)} />
            <Route path="notifications" element={WaitingComponent(NotificationPage, { status })} />
            <Route path="profile" element={WaitingComponent(Profile, { status })} />
            <Route path="customer-groups" element={WaitingComponent(CustomerGroupList, { status })} />
            <Route path="pos-registers" element={WaitingComponent(POSRegister, { status })} />
            <Route path="report/inventory-valuations" element={WaitingComponent(InventoryValuation, { status })} />
            <Route element={<SopGuard />}>
              <Route path="report/sop" element={WaitingComponent(SopList, { status })} />
            </Route>
            <Route element={<HkdBooksGuard />}>
              <Route path="report/hkd-books" element={WaitingComponent(HkdBookList, { status })} />
              <Route path="report/hkd-books/declarations" element={WaitingComponent(HkdDeclarationList, { status })} />
              <Route
                path="report/hkd-books/s1a"
                element={WaitingComponent(HkdS1a, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s2a"
                element={WaitingComponent(HkdS2a, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s2b"
                element={WaitingComponent(HkdS2b, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s2c"
                element={WaitingComponent(HkdS2c, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s2d"
                element={WaitingComponent(HkdS2d, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s2e"
                element={WaitingComponent(HkdS2e, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/s3a"
                element={WaitingComponent(HkdS3a, {
                  status,
                  redirect: { name: 'Sổ sách kế toán HKD', link: '/report/hkd-books' },
                })}
              />
              <Route
                path="report/hkd-books/01-bk-htk"
                element={WaitingComponent(HkdStocktakeDeclaration, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
              <Route
                path="report/hkd-books/01-tkn-cnkd"
                element={WaitingComponent(HkdRevenueNotification, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
              <Route
                path="report/hkd-books/01-cnkd"
                element={WaitingComponent(HkdCnkdReturn, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
              <Route
                path="report/hkd-books/02-cnkd-tncn-qtt"
                element={WaitingComponent(HkdPitFinalization, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
              <Route
                path="report/hkd-books/01-bk-stk"
                element={WaitingComponent(HkdBkStkNotice, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
              <Route
                path="report/hkd-books/01-tb-ddkd"
                element={WaitingComponent(HkdTbDdkdNotice, {
                  status,
                  redirect: { name: 'Bảng kê, tờ khai', link: '/report/hkd-books/declarations' },
                })}
              />
            </Route>
            <Route path="categories" element={WaitingComponent(CategoryList, { status })} />
            <Route path="uoms" element={WaitingComponent(UOMList, { status })} />
            <Route path="tags" element={WaitingComponent(TagList, { status })} />
            <Route path="pos" element={WaitingComponentPOS(POS)} />
            <Route path="specializations" element={WaitingComponent(SpecializationList)} />
            {/* Prescription Template Page */}

            {/* Doctors Page */}
            <Route path="doctors" element={<Outlet />}>
              <Route index element={WaitingComponent(Doctor, { status })} />
              <Route
                path="create"
                element={WaitingComponent(DoctorForm, {
                  status,
                  redirect: { name: 'Bác sĩ', link: '/doctors' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(DoctorForm, {
                  status,
                  redirect: { name: 'Bác sĩ', link: '/doctors' },
                })}
              />
            </Route>

            {/* Customers Page */}
            <Route path="customers" element={<Outlet />}>
              <Route index element={WaitingComponent(CustomerList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(CustomerForm, {
                  status,
                  redirect: { name: 'Khách hàng', link: '/customers' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(CustomerForm, {
                  status,
                  redirect: { name: 'Khách hàng', link: '/customers' },
                })}
              />
            </Route>

            {/* EInvoices Page */}
            <Route path="einvoices" element={<Outlet />}>
              <Route index element={WaitingComponent(EInvoice, { status })} />

              <Route
                path=":id/:action"
                element={WaitingComponent(EInvoiceForm, {
                  status,
                  redirect: { name: 'Hoá đơn điện tử', link: '/einvoices' },
                })}
              />
            </Route>

            {/* Inventories Page */}
            <Route path="inventories" element={<Outlet />}>
              <Route index element={WaitingComponent(InventoryList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(InventoryForm, {
                  status,
                  redirect: { name: 'Kiểm kê', link: '/inventories' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(InventoryForm, {
                  status,
                  redirect: { name: 'Kiểm kê', link: '/inventories' },
                })}
              />
            </Route>

            {/* Scraps Page */}
            <Route path="scraps" element={<Outlet />}>
              <Route index element={WaitingComponent(ScrapList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(ScrapForm, { status, redirect: { name: 'Xuất hỏng', link: '/scraps' } })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(ScrapForm, { status, redirect: { name: 'Xuất hỏng', link: '/scraps' } })}
              />
            </Route>

            {/* Expenses (Phiếu chi) Page */}
            <Route path="expenses" element={<Outlet />}>
              <Route index element={WaitingComponent(ExpenseList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(ExpenseForm, { status, redirect: { name: 'Phiếu chi', link: '/expenses' } })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(ExpenseForm, { status, redirect: { name: 'Phiếu chi', link: '/expenses' } })}
              />
            </Route>

            {/* Pos Orders Page */}
            <Route path="pos-orders" element={<Outlet />}>
              <Route index element={WaitingComponent(POSOrderList, { status })} />
              <Route
                path=":id/show"
                element={WaitingComponent(POSOrderDetail, {
                  status,
                  redirect: { name: 'Lịch sử đơn hàng', link: '/pos-orders' },
                })}
              />
            </Route>

            {/* Pos Settings Page */}
            <Route path="pos-settings" element={<Outlet />}>
              <Route index element={WaitingComponent(POSSettingList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thêm bộ phím tắt', link: '/pos-settings' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thiết lập điểm bán', link: '/pos-settings' },
                })}
              />
            </Route>
            {/* Purchase Orders Page */}
            <Route path="purchase-orders" element={<Outlet />}>
              <Route index element={WaitingComponent(PurchaseOrderList, { status })} />
              <Route path="imports" element={WaitingComponent(PurchaseOrderImportList, { status })} />
              <Route
                path="imports/:id"
                element={WaitingComponent(PurchaseOrderImportPreview, {
                  status,
                  redirect: { name: 'Hóa đơn nhà cung cấp', link: '/purchase-orders/imports' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(PurchaseOrderDetail, {
                  status,
                  redirect: { name: 'Nhập hàng', link: '/purchase-orders' },
                })}
              />
              <Route
                path=":id/show"
                element={WaitingComponent(PurchaseOrderDetail, {
                  status,
                  redirect: { name: 'Nhập hàng', link: '/purchase-orders' },
                })}
              />
              <Route
                path=":poId/return/new"
                element={WaitingComponent(SupplierReturnCreate, {
                  status,
                  redirect: { name: 'Chi tiết đơn mua', link: -1 },
                })}
              />
            </Route>

            {/* Receipts Page */}
            <Route path="receipts" element={<Outlet />}>
              <Route index element={WaitingComponent(ReceiptList, { status })} />
              <Route
                path=":id/show"
                element={WaitingComponent(ReceiptDetail, {
                  status,
                  redirect: { name: 'Phiếu nhập kho', link: '/receipts' },
                })}
              />
            </Route>

            {/* Supplier Returns Page */}
            <Route path="supplier-returns" element={<Outlet />}>
              <Route index element={WaitingComponent(SupplierReturnList, { status })} />
              <Route
                path=":id/show"
                element={WaitingComponent(SupplierReturnDetail, {
                  status,
                  redirect: { name: 'Trả hàng nhà cung cấp', link: '/supplier-returns' },
                })}
              />
            </Route>

            {/* Pos Settings Page */}
            <Route path="pos-settings" element={WaitingComponent(POSSettingList, { status })}>
              <Route
                path="pos-settings/:id/edit"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thiết lập điểm bán', link: '/pos-settings' },
                })}
              />
            </Route>

            {/* Quality Control Page */}
            <Route path="quality-controls" element={<Outlet />}>
              <Route
                index
                element={WaitingComponent(QualityControl, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(QuantityControlForm, {
                  status,
                  redirect: { name: 'Danh sách quản lý chất lượng', link: '/quality-controls' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(QuantityControlForm, {
                  status,
                  redirect: { name: 'Danh sách quản lý chất lượng', link: '/quality-controls' },
                })}
              />
            </Route>

            {/* ADR Page */}
            <Route path="sop/adr" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(AdrList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(AdrForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi ADR', link: '/sop/adr' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(AdrForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi ADR', link: '/sop/adr' },
                })}
              />
            </Route>

            {/* HygieneLog Page */}
            <Route path="sop/hygiene" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(HygieneLogList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(HygieneLogForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi vệ sinh', link: '/sop/hygiene' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(HygieneLogForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi vệ sinh', link: '/sop/hygiene' },
                })}
              />
            </Route>

            {/* Storage temperature/humidity register (#13) */}
            <Route path="sop/temperature-humidity" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(TemperatureHumidityBook, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>

            {/* RecallNotice Page */}
            <Route path="sop/recall" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(RecallNoticeList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(RecallNoticeForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi thuốc thu hồi', link: '/sop/recall' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(RecallNoticeForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi thuốc thu hồi', link: '/sop/recall' },
                })}
              />
            </Route>

            {/* InvalidPrescription Page */}
            <Route path="sop/invalid-rx" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(InvalidPrescriptionList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(InvalidPrescriptionForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi đơn thuốc không hợp lệ', link: '/sop/invalid-rx' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(InvalidPrescriptionForm, {
                  status,
                  redirect: { name: 'Sổ theo dõi đơn thuốc không hợp lệ', link: '/sop/invalid-rx' },
                })}
              />
            </Route>

            {/* Controlled-substance XNT registers (#11 thuốc độc / #12 KSĐB) */}
            <Route path="sop/toxic-controlled" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(ControlledRegisterList, {
                  status,
                  book: 'toxic',
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>
            <Route path="sop/special-controlled" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(ControlledRegisterList, {
                  status,
                  book: 'special',
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>

            {/* Drug receipts register (#1 Sổ nhập thuốc) */}
            <Route path="sop/drug-receipts" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(DrugReceiptsList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>

            {/* Rx-sales register (#2 Sổ bán thuốc theo đơn) */}
            <Route path="sop/prescription-sales" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(PrescriptionSalesList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>

            {/* Customer-detail register (#8 Sổ thông tin chi tiết khách hàng) */}
            <Route path="sop/customer-detail" element={<SopGuard />}>
              <Route
                index
                element={WaitingComponent(CustomerDetailList, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
            </Route>

            {/* Customer Complaint Page */}
            <Route path="customer-complaints" element={<Outlet />}>
              <Route
                index
                element={WaitingComponent(CustomerComplaint, {
                  status,
                  redirect: { name: 'Quy trình GPP', link: '/report/sop' },
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(CustomerComplaintForm, {
                  status,
                  redirect: { name: 'Phiếu theo dõi xử lý khiếu nại', link: '/customer-complaints' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(CustomerComplaintForm, {
                  status,
                  redirect: { name: 'Phiếu theo dõi xử lý khiếu nại', link: '/customer-complaints' },
                })}
              />
            </Route>

            {/* Patient Tracking Page */}
            <Route
              path="patient-tracking"
              element={WaitingComponent(PatientTracking, {
                status,
                redirect: { name: 'Quy trình GPP', link: '/report/sop' },
              })}
            />

            {/* Settings Page */}
            <Route path="settings" element={<Outlet />}>
              <Route index element={WaitingComponent(Setting, { status })} />
              <Route
                path="store"
                element={WaitingComponent(StoreSettingPage, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route path="prescription-templates" element={<Outlet />}>
                <Route
                  index
                  element={WaitingComponent(PrescriptionTemplate, {
                    status,
                    redirect: { name: 'Cấu hình', link: '/settings' },
                  })}
                />
                <Route
                  path="create"
                  element={WaitingComponent(PrescriptionTemplateForm, {
                    status,
                    redirect: { name: 'Danh sách đơn mẫu', link: '/settings/prescription-templates' },
                  })}
                />
                <Route
                  path=":id/edit"
                  element={WaitingComponent(PrescriptionTemplateForm, {
                    status,
                    redirect: { name: 'Danh sách đơn mẫu', link: '/settings/prescription-templates' },
                  })}
                />
              </Route>
              <Route
                path="general"
                element={WaitingComponent(GeneralSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="inventory"
                element={WaitingComponent(InventorySetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="taxes"
                element={WaitingComponent(TaxSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="payments"
                element={WaitingComponent(PaymentSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route path="staffs" element={<Outlet />}>
                <Route
                  index
                  element={WaitingComponent(SettingStaff, {
                    status,
                    redirect: { name: 'Cấu hình', link: '/settings' },
                  })}
                />
                <Route
                  path="create"
                  element={WaitingComponent(AddNewStaff, {
                    status,
                    redirect: { name: 'Nhân viên', link: '/settings/staffs' },
                  })}
                />
                <Route
                  path=":id/edit"
                  element={WaitingComponent(EditStaff, {
                    status,
                    redirect: { name: 'Nhân viên', link: '/settings/staffs' },
                  })}
                />
              </Route>
              <Route
                path="einvoices"
                element={WaitingComponent(EInvoiceSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="hkd-books"
                element={WaitingComponent(HkdBookSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="expense-categories"
                element={WaitingComponent(ExpenseCategorySetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="hkd-books/opening-balances"
                element={WaitingComponent(HkdOpeningBalance, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="integrations/dav/:tab?"
                element={WaitingComponent(DavIntegrationPage, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
            </Route>

            {/* Products Page */}
            <Route path="products" element={<Outlet />}>
              <Route index element={WaitingComponent(ProductList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(ProductForm, {
                  status,
                  redirect: { name: 'Sản phẩm', link: '/products' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(ProductForm, {
                  status,
                  redirect: { name: 'Sản phẩm', link: '/products' },
                })}
              />
            </Route>

            {/* Vendors Page */}
            <Route path="vendors" element={<Outlet />}>
              <Route index element={WaitingComponent(VendorList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(VendorForm, {
                  status,
                  redirect: { name: 'Nhà cung cấp', link: '/vendors' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(VendorForm, {
                  status,
                  redirect: { name: 'Nhà cung cấp', link: '/vendors' },
                })}
              />
            </Route>

            {/* Demo Pages */}
            {isDevelopment && (
              <>
                <Route path="demo/inbox" element={WaitingComponentPOS(InboxDemo, { status })} />
              </>
            )}

            {/* Omnichannel chat console — local and dev only */}
            {isChatEnabled && <Route path="chat" element={WaitingComponentPOS(ChatConsole)} />}

            {/* Demo V2 Pages — always accessible for client demos */}
            <Route path="demo/v2/setup-pharmacy" element={WaitingComponentPOS(DemoV2SetupPharmacy)} />
            <Route path="demo/v2/setup-clinic" element={WaitingComponentPOS(DemoV2SetupClinic)} />
            <Route path="demo/v2/setup-pharma-company" element={WaitingComponentPOS(DemoV2SetupPharmaCompany)} />
            <Route path="demo/v2/calendar" element={WaitingComponent(DemoV2Calendar, { status })} />
            <Route path="demo/v2/clinic-queue" element={WaitingComponent(DemoV2ClinicQueue, { status })} />
            <Route path="demo/v2/clinic-website" element={WaitingComponent(DemoV2ClinicWebsite, { status })} />
            <Route path="demo/v2/clinic-roadmap" element={WaitingComponent(DemoV2ClinicRoadmap, { status })} />
            <Route
              path="demo/v2/clinic-features"
              element={
                <Suspense fallback={<Loading />}>
                  <DemoV2ClinicFeatures />
                </Suspense>
              }
            />

            {/* 404 Page */}
            <Route path="*" element={<Navigate replace to="/404" />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
